/**
 * Currency Context
 * 
 * Global state management for user currency preferences and exchange rates
 * Provides currency conversion and formatting functions throughout the app
 * Subscribes to Firestore for real-time exchange rate updates
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { isTargetViewerRole } from '../utils/authRoles';
import { CurrencyCode, CurrencyContextState, CurrencyDisplayOptions, Money, ExchangeRates } from '../types/currency.types';
import { EXCHANGE_RATES, DEFAULT_CURRENCY } from '../constants/currencies';
import { logger } from '../utils/logger';
import {
  normalizeToUSD as utilNormalizeToUSD,
  convertMoney as utilConvertMoney,
  formatMoney as utilFormatMoney,
} from '../utils/currencyUtils';
import toast from 'react-hot-toast';

const CurrencyContext = createContext<CurrencyContextState | undefined>(undefined);

/**
 * Hook to access currency context
 * 
 * Usage:
 * ```tsx
 * const { preferredCurrency, formatMoney, convertMoney } = useCurrency();
 * ```
 */
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

/**
 * Currency Provider Component
 * 
 * Wraps the app to provide currency context to all components
 * Loads user's preferred currency from their profile
 * Persists changes to Firestore
 */
export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const userPreferredCurrency = typeof (user as any)?.preferredCurrency === 'string'
    ? ((user as any).preferredCurrency as CurrencyCode)
    : undefined;
  const [preferredCurrency, setPreferredCurrencyState] = useState<CurrencyCode>(DEFAULT_CURRENCY);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(EXCHANGE_RATES);
  const [isInitialized, setIsInitialized] = useState(false);
  const pendingPreferenceRef = useRef<CurrencyCode | null>(null);
  const currentUserUidRef = useRef<string | null>(null);

  // Load user's preferred currency on mount
  useEffect(() => {
    if (!user?.uid) {
      pendingPreferenceRef.current = null;
      currentUserUidRef.current = null;
      setPreferredCurrencyState(DEFAULT_CURRENCY);
      logger.debug('[Currency] No user, defaulting to:', DEFAULT_CURRENCY);
      setIsInitialized(true);
      return;
    }

    if (currentUserUidRef.current !== user.uid) {
      pendingPreferenceRef.current = null;
      currentUserUidRef.current = user.uid;
    }

    const validCodes: CurrencyCode[] = ['AUD', 'USD', 'GBP', 'NZD'];
    const resolvedUserCurrency = userPreferredCurrency && validCodes.includes(userPreferredCurrency)
      ? userPreferredCurrency
      : DEFAULT_CURRENCY;

    // Avoid stale auth snapshots clobbering a newly-selected currency.
    if (pendingPreferenceRef.current && resolvedUserCurrency !== pendingPreferenceRef.current) {
      logger.debug('[Currency] Ignoring stale user preference snapshot:', resolvedUserCurrency);
      setIsInitialized(true);
      return;
    }

    if (pendingPreferenceRef.current === resolvedUserCurrency) {
      pendingPreferenceRef.current = null;
    }

    setPreferredCurrencyState(resolvedUserCurrency);
    logger.debug('[Currency] Loaded user preference:', resolvedUserCurrency);
    setIsInitialized(true);
  }, [user?.uid, userPreferredCurrency]);

  // Subscribe to exchange rates from Firestore
  // Only subscribe when user is authenticated (Firestore rules require auth)
  useEffect(() => {
    // If no user, use fallback constants
    if (!user) {
      setExchangeRates(EXCHANGE_RATES);
      logger.debug('[Currency] No user authenticated, using fallback exchange rates');
      return;
    }

    // Viewers cannot read systemSettings/exchangeRates — do not subscribe.
    if (isTargetViewerRole(user.role)) {
      setExchangeRates(EXCHANGE_RATES);
      logger.debug('[Currency] Target viewer: using fallback exchange rates');
      return;
    }

    // User is authenticated, subscribe to real-time rates
    const unsubscribe = onSnapshot(
      doc(db, 'systemSettings/exchangeRates'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.rates) {
            setExchangeRates(data.rates);
            logger.debug('[Currency] Exchange rates updated from Firestore:', data.rates);
          }
        }
      },
      (error) => {
        logger.error('[Currency] Error subscribing to exchange rates:', error);
        // Fallback to hardcoded constants on error
        setExchangeRates(EXCHANGE_RATES);
      }
    );

    return () => unsubscribe();
  }, [user]);

  /**
   * Update user's preferred currency
   * Persists to Firestore user document
   */
  const setPreferredCurrency = async (currency: CurrencyCode): Promise<void> => {
    const previousCurrency = preferredCurrency;
    try {
      logger.debug('[Currency] Setting preferred currency:', currency);
      
      // Update local state immediately for responsiveness
      pendingPreferenceRef.current = currency;
      setPreferredCurrencyState(currency);
      
      // Persist to Firestore if user is logged in
      if (user?.uid) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          preferredCurrency: currency,
          updatedAt: new Date(),
        });
        
        toast.success(`Currency preference updated to ${currency}`);
        logger.debug('[Currency] Saved to Firestore:', currency);
      } else {
        pendingPreferenceRef.current = null;
        logger.debug('[Currency] Not logged in, local state only');
      }
    } catch (error) {
      logger.error('[Currency] Error saving preference:', error);
      toast.error('Failed to save currency preference');
      // Revert on error
      pendingPreferenceRef.current = null;
      setPreferredCurrencyState(previousCurrency);
      throw error;
    }
  };

  /**
   * Format money with user's preferred currency
   * 
   * @param value - Amount to format (number or Money object)
   * @param sourceCurrency - Currency of the value (if number)
   * @param options - Display options
   * @returns Formatted string
   */
  const formatMoney = (
    value: number | Money | null | undefined,
    sourceCurrency?: CurrencyCode,
    options?: CurrencyDisplayOptions
  ): string => {
    return utilFormatMoney(value, sourceCurrency, preferredCurrency, options, exchangeRates);
  };

  /**
   * Convert money between currencies
   * 
   * @param amount - Amount to convert
   * @param from - Source currency
   * @param to - Target currency
   * @returns Converted amount
   */
  const convertMoney = (amount: number, from: CurrencyCode, to: CurrencyCode): number => {
    return utilConvertMoney(amount, from, to, exchangeRates);
  };

  /**
   * Normalize amount to USD
   * 
   * @param amount - Amount in source currency
   * @param currency - Source currency
   * @returns Amount in USD
   */
  const normalizeToUSD = (amount: number, currency: CurrencyCode): number => {
    return utilNormalizeToUSD(amount, currency, exchangeRates);
  };

  const value: CurrencyContextState = {
    preferredCurrency,
    exchangeRates,
    setPreferredCurrency,
    formatMoney,
    convertMoney,
    normalizeToUSD,
  };

  // Don't render children until currency preference is loaded
  // This prevents flickering when currency changes
  if (!isInitialized) {
    return null;
  }

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
